import { useCallback, useEffect, useState } from "react";
import { api, type User } from "./lib/api.ts";
import { LoginPage } from "./pages/LoginPage.tsx";
import { InvitePage } from "./pages/InvitePage.tsx";
import { PairPage } from "./pages/PairPage.tsx";
import { HomePage } from "./pages/HomePage.tsx";
import { SettingsPage } from "./pages/SettingsPage.tsx";
import { ProjectPage } from "./pages/ProjectPage.tsx";
import { RelayProvider } from "./lib/relayContext.tsx";
import { navigate, useRoute } from "./lib/router.ts";
import { contextFromRoute, rememberProject } from "./lib/projectContext.ts";
import { extractPairingCode, getPendingPairingCode, setPendingPairingCode } from "./lib/pairingUrl.ts";

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const route = useRoute();

  // Remember where the user was working so Settings / Usage / Home can offer "Back to project".
  useEffect(() => {
    const ctx = contextFromRoute(route);
    if (ctx) rememberProject(ctx);
  }, [route]);

  // If the user lands on a URL with a #code=... fragment, preserve it in sessionStorage
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      const code = extractPairingCode(window.location.hash);
      if (code) {
        setPendingPairingCode(code);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    api
      .session()
      .then((s) => setUser(s.authenticated && s.user ? s.user : null))
      .catch(() => setUser(null));
  }, []);
  useEffect(refresh, [refresh]);
  const onAuthLost = useCallback(() => setUser(null), []);

  if (user === undefined) return <div className="center muted">Loading…</div>;

  if (route.name === "invite") {
    return (
      <InvitePage
        onRegistered={(u) => {
          setUser(u);
          const pendingPairing = getPendingPairingCode();
          if (pendingPairing) {
            navigate("/pair");
          } else {
            navigate("/");
          }
        }}
      />
    );
  }
  if (!user) {
    return (
      <LoginPage
        onLogin={(u) => {
          setUser(u);
          const pendingPairing = getPendingPairingCode();
          if (pendingPairing && route.name !== "pair") {
            navigate("/pair");
          }
        }}
      />
    );
  }

  const logout = () => {
    void api
      .logout()
      .catch(() => undefined)
      .then(() => {
        setUser(null);
        navigate("/");
      });
  };

  return (
    <RelayProvider key={user.id} onAuthLost={onAuthLost}>
      {route.name === "pair" && <PairPage user={user} onLogout={logout} />}
      {route.name === "home" && <HomePage user={user} onLogout={logout} />}
      {route.name === "settings" && <SettingsPage user={user} section={route.section} onLogout={logout} />}
      {route.name === "project" && (
        <ProjectPage
          user={user}
          machineId={route.machineId}
          projectId={route.projectId}
          chatId={route.chatId}
          onLogout={logout}
        />
      )}
    </RelayProvider>
  );
}
